import { FunctionComponent } from 'react';
import styled from 'styled-components';
import Link from 'next/link';
import { PartnersSection3Cases } from '../../types/contentstack';
import { getAssetUrl } from '../../utils/contentstack';
import { mediaScreen } from '../../utils/mediaScreen';
import { Col, ResponsiveContainer, Row } from '../responsive';
import { Icon } from '../ui/icon';

export const PartnerCaseStudy: FunctionComponent<{
  data: PartnersSection3Cases;
  index?: number;
}> = ({ data, index = 1 }) => {
  const logoUrl = getAssetUrl(data.partner_logoConnection);
  const benefits = (data.how_mapped_helps?.benefits || []).filter(Boolean);
  const outcome = data.how_mapped_helps?.outcome;
  const quote = data.quote;
  const hasOutcome = outcome?.label && outcome?.text;
  const hasQuote = quote?.text && quote?.author;
  const numberSrc = `/img/number-${Math.min(index, 4)}.svg`;

  return (
    <CaseStudySection>
      <ResponsiveContainer>
        <Row>
          {/* Left: Partner info */}
          <Col xs={12} md={5} className="left-col">
            <div className="case-header">
              <img className="number-svg" src={numberSrc} alt="" />

              <div className="logo-wrapper">
                {logoUrl && (
                  <img className="partner-logo" src={logoUrl} alt="" />
                )}
              </div>
              {data.headline && <h2 className="headline">{data.headline}</h2>}
              {data.subheadline && (
                <p className="subheadline">{data.subheadline}</p>
              )}
              {data.partner_link?.href && (
                <Link href={data.partner_link.href} legacyBehavior passHref>
                  <a className="partner-link">{data.partner_link.title}</a>
                </Link>
              )}
            </div>
          </Col>

          {/* Right: How Mapped Helps */}
          <Col xs={12} md={7} className="right-col">
            {data.how_mapped_helps && (benefits.length > 0 || hasOutcome) && (
              <HelpPanel>
                {data.how_mapped_helps.title && (
                  <div className="help-title-bar">
                    <h4 className="help-title">
                      {data.how_mapped_helps.title}
                    </h4>
                  </div>
                )}

                {benefits.length > 0 && (
                  <div className="benefits-grid">
                    {benefits.map((benefit, i) => (
                      <div key={i} className="benefit">
                        {benefit}
                      </div>
                    ))}
                  </div>
                )}

                {hasOutcome && (
                  <div className="outcome">
                    {outcome?.label && (
                      <span className="outcome-label">{outcome.label}</span>
                    )}
                    {outcome?.text && (
                      <span className="outcome-text">{outcome.text}</span>
                    )}
                  </div>
                )}
              </HelpPanel>
            )}
          </Col>
        </Row>

        {/* Quote */}
        {hasQuote && (
          <QuoteSection>
            <Row>
              <Col xs={12} md={4} style={{ position: 'relative' }}>
                <img
                  className="quote-image"
                  src="/img/quotes-image.svg"
                  alt=""
                />
              </Col>
              <Col xs={12} md={8}>
                {quote?.eyebrow && (
                  <h5 className="quote-eyebrow">{quote.eyebrow}</h5>
                )}
                {quote?.text && (
                  <p
                    className="quote-text"
                    dangerouslySetInnerHTML={{ __html: quote.text }}
                  />
                )}
                <div className="quote-meta">
                  {quote?.author && (
                    <p
                      className="quote-author"
                      dangerouslySetInnerHTML={{ __html: quote.author }}
                    />
                  )}
                  {quote?.link?.href && (
                    <div className="quote-link-wrapper">
                      <Link href={quote.link.href} legacyBehavior passHref>
                        <a className="quote-link">
                          {quote.link.title}
                          <Icon name="arrow-right" size={16} />
                        </a>
                      </Link>
                    </div>
                  )}
                </div>
              </Col>
            </Row>
          </QuoteSection>
        )}
      </ResponsiveContainer>
    </CaseStudySection>
  );
};

const CaseStudySection = styled.section`
  padding: 80px 0;
  background: #fff;

  .left-col {
    display: flex;
    flex-direction: column;
  }

  .case-header {
    padding-right: 40px;
    position: relative;
  }

  .logo-wrapper {
    position: relative;
    margin-bottom: 48px;
  }

  .number-svg {
    position: absolute;
    top: -40px;
    left: -60px;
    height: 250px;
    width: auto;
    opacity: 1;
    pointer-events: none;
  }

  .partner-logo {
    position: relative;
    height: 80px;
    object-fit: contain;
    object-position: left;
  }

  .headline {
    font-size: 50px;
    line-height: 1.2;
    font-weight: 700;
    color: ${({ theme }) => theme.colors.slate1};
    margin: 0 0 24px;
    max-width: 600px;
  }

  .subheadline {
    font-size: 18px;
    line-height: 32px;
    font-weight: 400;
    color: #4d5f80;
    margin: 0 0 20px;
    max-width: 650px;
  }

  .partner-link {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 16px;
    font-weight: 500;
    color: ${({ theme }) => theme.colors.eb1};
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }

  ${mediaScreen('xs', 'md')} {
    padding: 48px 0;

    .case-header {
      padding-right: 0;
      margin-bottom: 32px;
    }

    .number-svg {
      height: 200px;
      top: -20px;
      left: auto;
      right: -15px;
    }

    .headline {
      font-size: 32px;
      line-height: 1.2;
      margin-bottom: 16px;
    }

    .partner-logo {
      max-height: 50px;
    }

    .logo-wrapper {
      margin-bottom: 32px;
    }

    .subheadline {
      font-size: 16px;
      line-height: 22px;
    }
  }
`;

const HelpPanel = styled.div`
  .help-title-bar {
    background-image: url('/img/accordion-gradient.svg');
    background-repeat: no-repeat;
    background-position: center left;
    background-size: cover;
    padding: 16px 24px;
    border-radius: 8px;
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
    margin-bottom: 24px;
    border-bottom: 1px solid #e0e5ec;
  }

  .help-title {
    font-size: 20px;
    font-weight: 700;
    color: ${({ theme }) => theme.colors.slate1};
    margin: 0;
  }

  .benefits-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 24px 48px;
    padding: 0 8px;
  }

  .benefit {
    font-size: 18px;
    line-height: 1.5;
    font-weight: 700;
    color: ${({ theme }) => theme.colors.slate1};
    padding-bottom: 20px;
    border-bottom: 1px solid #e0e5ec;
  }

  .outcome {
    background: ${({ theme }) => theme.colors.slate1};
    border-radius: 8px;
    border-top-left-radius: 0;
    border-top-right-radius: 0;
    padding: 24px 32px;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .outcome-label {
    font-size: 14px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 1px;
    color: ${({ theme }) => theme.colors.teal};
  }

  .outcome-text {
    font-size: 18px;
    line-height: 1.5;
    font-weight: 700;
    color: #fff;
  }

  ${mediaScreen('xs', 'md')} {
    .benefits-grid {
      grid-template-columns: 1fr;
      gap: 16px;
      padding: 0;
    }

    .benefit {
      font-size: 16px;
      padding-bottom: 16px;
      padding-left: 24px;
      padding-right: 24px;
    }

    .outcome {
      padding: 20px 24px;
    }

    .outcome-text {
      font-size: 16px;
    }
  }
`;

const QuoteSection = styled.div`
  padding: 160px 0 0;

  .quote-image {
    max-width: unset;
    position: relative;
    width: 120%;
    top: -5%;

    ${mediaScreen('md', 'xl')} {
      top: 5%;
      width: 140%;
    }
    ${mediaScreen('sm', 'lg')} {
      width: 130%;
      top: 10%;
      left: -20%;
    }
    ${mediaScreen('xs', 'md')} {
      width: 70%;
      opacity: 0.8;
      margin-bottom: -30px;
      margin-top: -100px;
    }
  }

  .quote-eyebrow {
    letter-spacing: 4px;
    font-size: 16px;
    font-weight: 700;
    color: ${({ theme }) => theme.colors.eb1};
    margin: 0 0 40px;
    text-transform: uppercase;

    ${mediaScreen('xs', 'md')} {
      font-size: 14px;
      margin-bottom: 24px;
    }
  }

  .quote-text {
    font-size: 36px;
    line-height: 1.48;
    font-weight: 400;
    margin: 0 0 40px;
    color: ${({ theme }) => theme.colors.slate1};
    max-width: 880px;

    ${mediaScreen('xs', 'md')} {
      font-size: 24px;
      line-height: 1.45;
      margin-bottom: 32px;
    }
  }

  .quote-meta {
    ${mediaScreen('sm')} {
      display: flex;
      justify-content: space-between;
      align-items: flex-end;

      > * {
        margin: 0;
      }
    }
  }

  .quote-author {
    font-size: 16px;
    line-height: 1.5;
    margin: 0 0 28px;
    color: ${({ theme }) => theme.colors.eb1};
    font-weight: 400;
    max-width: 370px;

    ${mediaScreen('xs', 'md')} {
      margin-bottom: 20px;
    }
  }

  .quote-link-wrapper {
    margin-bottom: 16px;
  }

  .quote-link {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 16px;
    font-weight: 300;
    color: ${({ theme }) => theme.colors.slate1};
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
`;
